import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Navbar, Nav, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';
import CookieConsent from '../components/CookieConsent';
import VersionFooter from '../components/VersionFooter';
import LanguageSelector from '../components/LanguageSelector';
import { getSmartRedirectPath } from '../shared/accountCache';
import SEO from '../components/SEO';

export default function HomePage() {
  const { t } = useTranslation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  

  const handleAuthModalClose = () => setShowAuthModal(false);
  const handleAuthModalShow = () => setShowAuthModal(true);
  
  // Check for redirect parameter and open login modal if user is not logged in
  useEffect(() => {
    const redirectParam = searchParams.get('redirect');
    if (redirectParam && !currentUser) {
      setShowAuthModal(true);
    }
  }, [searchParams, currentUser]);
  
  const handleGetStarted = async () => {
    if (currentUser) {
      // Use smart redirect logic instead of hardcoded /plans (use cache, don't force refresh)
      const redirectPath = await getSmartRedirectPath(currentUser, false);
      console.error('[HomePage] Get Started → redirecting to:', redirectPath);
      navigate(redirectPath);
    } else {
      handleAuthModalShow();
    }
  };

  return (
    <div className="home-page-container">
      <SEO
        title={t('homePage.heroTitle', { defaultValue: 'Organize, Manage, and Share Your Trip' })}
        description={t('homePage.heroDescription1', { 
          defaultValue: 'trip147 is your all-in-one platform to manage, organize, and easily share your travel itinerary. Keep every detail of your trip in one place—hotels, attractions, transportation, costs, to-do lists, and more.' 
        })}
        keywords="trip planner, travel itinerary, trip organizer, travel planning, vacation planner, trip management, travel app, itinerary planner, AI trip planning"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "trip147",
          "description": t('homePage.heroDescription1', { 
            defaultValue: 'trip147 is your all-in-one platform to manage, organize, and easily share your travel itinerary.' 
          }),
          "url": "https://trip147.com",
          "applicationCategory": "TravelApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
          }
        }}
      />
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand href="/" className="fw-bold fs-4 d-flex align-items-center">
            <img src="/logo.svg" alt="trip147" style={{ width: '40px', height: '40px' }} className="me-2" />
            <span className="d-none d-sm-inline">trip147</span>
          </Navbar.Brand>
          <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
            <LanguageSelector variant="outline-secondary" size="sm" />
          </div>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
            <Nav className="d-flex flex-column flex-md-row align-items-center gap-2">
              {currentUser ? (
                <>
                  <Button 
                    variant="outline-primary" 
                    onClick={async () => {
                      const redirectPath = await getSmartRedirectPath(currentUser);
                      navigate(redirectPath);
                    }}
                    className="w-100 w-md-auto text-nowrap"
                  >
                    {t('homePage.myTrips')}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={logout}
                    className="w-100 w-md-auto text-nowrap"
                  >
                    {t('navigation.logout')}
                  </Button>
                </>
              ) : (
                <Button 
                  variant="primary" 
                  onClick={handleAuthModalShow}
                  className="w-100 w-md-auto text-nowrap"
                >
                  {t('homePage.loginSignUp')}
                </Button>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="hero-section pt-3 pb-5 bg-light">
        <Container>
          <Row className="align-items-center py-5">
            <Col md={6} className="text-center text-md-start">
              <h1 className="display-4 fw-bold mb-4">{t('homePage.heroTitle')}</h1>
              <p className="lead mb-4">
                {t('homePage.heroDescription1')}
              </p>
              <p className="mb-4">
                {t('homePage.heroDescription2')}
              </p>
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleGetStarted}
                className="me-3 mb-3"
              >
                <i className="bi bi-rocket-takeoff me-2"></i>
                {t('homePage.getStartedFree')}
              </Button>
              <Button 
                variant="outline-secondary" 
                size="lg" 
                href="#features"
                className="ms-md-0 mb-3"
              >
                {t('homePage.learnMore')}
              </Button>
            </Col>
            <Col md={6} className="text-center">
              <div className="ratio ratio-16x9 rounded shadow-lg">
                <iframe
                  src="https://www.youtube.com/embed/fPmgeiRNCd0?quality=hd1080"
                  title="trip147 Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded"
                ></iframe>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section id="examples" className="py-2 py-md-5 bg-white">
        <Container>
          <h2 className="text-center mb-4 fw-bold">{t('homePage.seeItInAction')}</h2>
          <p className="text-center text-muted mb-5 lead">
            {t('homePage.seeItInActionDescription')}
          </p>
          <Row className="justify-content-center">
            <Col md={6} lg={5} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex flex-column">
                  <div className="text-center mb-3">
                    <div className="mb-3">
                      <img src="/images/sw.jpeg"  alt="Switzerland Trip" style={{ objectFit: 'cover', borderRadius: '8px' }} />
                    </div>
                    <h4 className="mb-2">{t('homePage.switzerlandTrip')}</h4>
                  </div>
                  <Button 
                    variant="primary" 
                    href="https://trip147.com/share/plan/16a7d22e-785c-48af-bb08-d2d3d14888f9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto"
                  >
                    <i className="bi bi-box-arrow-up-right me-2"></i>{t('homePage.viewItinerary')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={5} className="mb-4">
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex flex-column">
                  <div className="text-center mb-3">
                    <div className="mb-3">
                      <img src="/images/bu.jpeg"  alt="Bucharest Adventure" style={{ objectFit: 'cover', borderRadius: '8px' }} />
                    </div>
                    <h4 className="mb-2">{t('homePage.bucharestAdventure')}</h4>
                  </div>
                  <Button 
                    variant="primary" 
                    href="https://trip147.com/share/plan/04e3f5a9-e9db-44e3-b8f1-3e7d7e73ca3d"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto"
                  >
                    <i className="bi bi-box-arrow-up-right me-2"></i>
                    {t('homePage.viewItinerary')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="luggage-qr" className="py-2 py-md-5 bg-white">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="order-2 order-md-1">
              <div className="d-flex align-items-center mb-3">
                <span className="badge bg-success me-2" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}>
                  <i className="bi bi-star-fill me-1"></i>
                  {t('homePage.newFeature')}
                </span>
                <h2 className="fw-bold mb-0">{t('homePage.neverLoseLuggage')}</h2>
              </div>
              <p className="lead mb-4">
                {t('homePage.luggageDescription')}
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill text-primary me-2"></i>
                  <strong>{t('homePage.quickRecovery')}</strong> {t('homePage.quickRecoveryText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill text-primary me-2"></i>
                  <strong>{t('homePage.multipleFormats')}</strong> {t('homePage.multipleFormatsText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill text-primary me-2"></i>
                  <strong>{t('homePage.unlimitedTags')}</strong> {t('homePage.unlimitedTagsText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill text-primary me-2"></i>
                  <strong>{t('homePage.easyPrinting')}</strong> {t('homePage.easyPrintingText')}
                </li>
              </ul>
            </Col>
            <Col md={6} className="text-center mb-4 mb-md-0 order-1 order-md-2">
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '30px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                <img 
                  src="/images/qr-tag1.png" 
                  alt="QR Code Luggage Tag" 
                  style={{ maxWidth: '300px', width: '100%', height: 'auto' }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="features" className="py-5 bg-light">
        <Container>
          <h2 className="text-center mb-2 mb-md-4 fw-bold">{t('homePage.whyChooseTrip147')}</h2>
          <Row>
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-calendar-check fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.allTripInfoOrganized')}</h3>
                <p className="text-muted">
                  {t('homePage.allTripInfoText')}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-stars fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.aiPoweredPlanning')}</h3>
                <p className="text-muted">
                  {t('homePage.aiPoweredPlanningText')}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-share fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.easySharingCollaboration')}</h3>
                <p className="text-muted">
                  {t('homePage.easySharingText')}
                </p>
              </div>
            </Col>
          </Row>
          <Row className="mt-1 mt-md-4">
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-map fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.interactiveMaps')}</h3>
                <p className="text-muted">
                  {t('homePage.interactiveMapsText')}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-cloud-sun fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.weatherInformation')}</h3>
                <p className="text-muted">
                  {t('homePage.weatherInformationText')}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center h-100">
                <div className="feature-icon mb-1 mb-md-2 text-primary">
                  <i className="bi bi-printer fs-1"></i>
                </div>
                <h3 className="mb-2 mb-md-3">{t('homePage.printExport')}</h3>
                <p className="text-muted">
                  {t('homePage.printExportText')}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <VersionFooter />

      <AuthModal 
        show={showAuthModal} 
        handleClose={handleAuthModalClose}
        redirectPath={searchParams.get('redirect')}
      />
      
      <CookieConsent />
    </div>
  );
} 